import { ArrowLeftIcon } from '@radix-ui/react-icons'
import { Link } from 'react-router'
import {
	eventEnd,
	formatClockSpan,
	formatDayMonth,
	formatWeekday,
	isEventPassed,
	parseSafeZone,
	performersDisplay,
	safeZoneImageStyle,
	venueLabel,
} from '#app/domains/program/pure'
import { isSoldOut, seatsRemaining } from '#app/domains/tickets/pure'
import { type EventPerformer } from '#app/utils/providers/events.server.js'

type HeroEvent = {
	id: string
	title: string
	date: Date | string
	endsAt?: Date | string | null
	dropIn?: boolean | null
	imageUrl?: string | null
	imageSafeZone?: string | null
	location?: string | null
	host?: string | null
	performers?: string | null
	featuring?: EventPerformer[] | null
	seats?: { total: number } | null
	tickets?: ReadonlyArray<{ paymentStatus?: string | null }> | null
	ticketCount?: number | null
}

/**
 * The concert's opening: a strip of night sky with the title (it morphs in
 * from the program card), when and where, and the photo in a lit frame —
 * or, without a photo, a glowing window.
 */
export function DetailHero({ event }: { event: HeroEvent }) {
	const passed = isEventPassed(eventEnd(event))
	const soldOut = !passed && isSoldOut(event)
	const remaining = seatsRemaining(event)
	const performers = performersDisplay({
		performers: event.performers ?? null,
		featuring: event.featuring ?? [],
	})
	const venue = venueLabel(event)

	return (
		<header
			className="ev-hero"
			data-has-photo={event.imageUrl ? '' : undefined}
		>
			<div className="ev-hero-inner">
				<div className="ev-hero-copy">
					<Link to="/#program" viewTransition className="ev-back">
						<ArrowLeftIcon aria-hidden="true" /> Programmet
					</Link>
					<p className="ev-eyebrow">
						<span className="ev-weekday">{formatWeekday(event.date)}</span>{' '}
						{formatDayMonth(event.date)}
					</p>
					<h1
						className="ev-title"
						// the program card's title flies in here (view transitions)
						style={{ viewTransitionName: `event-title-${event.id}` }}
					>
						{event.title}
					</h1>
					{performers ? <p className="ev-performers">{performers}</p> : null}
					<ul className="ev-facts">
						<li>
							<span className="ev-fact-label">Kl.</span>{' '}
							<time dateTime={new Date(event.date).toISOString()}>
								{formatClockSpan(event.date, event.endsAt)}
							</time>
						</li>
						{venue ? (
							<li>
								<span className="ev-fact-label">Kvar</span> {venue}
							</li>
						) : null}
						{event.dropIn ? (
							<li>
								<span className="ev-fact-label">Inngang</span> berre møt opp
							</li>
						) : remaining != null && !passed ? (
							<li>
								<span className="ev-fact-label">Ledig</span>{' '}
								{soldOut
									? 'utseld'
									: `${remaining} ${remaining === 1 ? 'plass' : 'plassar'}`}
							</li>
						) : null}
					</ul>
					{passed ? (
						<p className="ev-stamp">Fullført</p>
					) : soldOut ? (
						<p className="ev-stamp">Utseld</p>
					) : null}
				</div>
				{event.imageUrl ? (
					<figure className="ev-photo">
						<img
							src={event.imageUrl}
							alt={event.title}
							width={640}
							height={480}
							style={safeZoneImageStyle(
								parseSafeZone(event.imageSafeZone),
								4 / 3,
							)}
							decoding="async"
						/>
					</figure>
				) : (
					<div className="ev-window" aria-hidden="true">
						<span />
						<span />
						<span />
						<span />
					</div>
				)}
			</div>
		</header>
	)
}
